import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import GoogleSignInButton from './GoogleSignInButton';
import LandingNavbar from './common/LandingNavbar';
import {
  DEFAULT_PHONE_COUNTRY,
  PHONE_COUNTRIES,
  getPhoneCountry,
  isValidPhone,
  parseInternationalInput,
  toE164
} from '../utils/phone';
import './LandingPage.css';
import './SignupPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const SignupPage = () => {
  const { t, i18n } = useTranslation('landing');
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar';
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    phoneCountry: DEFAULT_PHONE_COUNTRY,
    password: '',
    confirmPassword: '',
    role: 'user' // default role
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [countryMenuOpen, setCountryMenuOpen] = useState(false);
  const countryPickerRef = useRef(null);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0, // 0-4 (0=weak, 1-2=medium, 3-4=strong)
    label: '', // 'Weak', 'Medium', 'Strong'
    color: '#ea4335', // red, yellow, green
    requirements: {
      minLength: false,
      hasUppercase: false,
      hasLowercase: false,
      hasNumber: false,
      hasSpecialChar: false
    }
  });

  // Calculate password strength in real-time
  const calculatePasswordStrength = (password) => {
    if (!password) {
      return {
        score: 0,
        label: '',
        color: '#ea4335',
        requirements: {
          minLength: false,
          hasUppercase: false,
          hasLowercase: false,
          hasNumber: false,
          hasSpecialChar: false
        }
      };
    }

    const requirements = {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };

    // Calculate score (0-4)
    let score = 0;
    if (requirements.minLength) score++;
    if (requirements.hasUppercase) score++;
    if (requirements.hasLowercase) score++;
    if (requirements.hasNumber) score++;
    if (requirements.hasSpecialChar) score++;

    // Bonus for length
    if (password.length >= 12) score += 0.5;
    if (password.length >= 16) score += 0.5;

    // Determine label and color
    let label = '';
    let color = '#ea4335'; // red

    if (score === 0) {
      label = '';
      color = '#ea4335';
    } else if (score <= 2) {
      label = 'signup.strengthWeak';
      color = '#ea4335'; // red
    } else if (score <= 3.5) {
      label = 'signup.strengthMedium';
      color = '#fbbc04'; // yellow/orange
    } else {
      label = 'signup.strengthStrong';
      color = '#34a853'; // green
    }

    return {
      score: Math.min(score, 4), // Cap at 4
      label,
      color,
      requirements
    };
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');

    // Update password strength in real-time
    if (name === 'password') {
      setPasswordStrength(calculatePasswordStrength(value));
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    const pasted = parseInternationalInput(value);
    setError('');

    if (pasted) {
      setFormData((prev) => ({
        ...prev,
        phoneCountry: pasted.iso,
        phone: pasted.national
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, phone: value.replace(/[^\d\s-]/g, '') }));
  };

  const handlePhoneCountryChange = (iso) => {
    setError('');
    setFormData((prev) => ({ ...prev, phoneCountry: iso }));
    setCountryMenuOpen(false);
  };

  useEffect(() => {
    if (!countryMenuOpen) return undefined;

    const onDocClick = (event) => {
      if (!countryPickerRef.current?.contains(event.target)) {
        setCountryMenuOpen(false);
      }
    };
    const onKey = (event) => {
      if (event.key === 'Escape') setCountryMenuOpen(false);
    };

    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [countryMenuOpen]);

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.first_name || formData.first_name.trim().length === 0) {
      setError(t('signup.errorFirstName'));
      return;
    }

    if (!formData.last_name || formData.last_name.trim().length === 0) {
      setError(t('signup.errorLastName'));
      return;
    }

    if (!formData.phone || formData.phone.trim().length === 0) {
      setError(t('signup.errorPhone'));
      return;
    }

    if (!isValidPhone(formData.phoneCountry, formData.phone)) {
      setError(t('signup.errorPhoneInvalid'));
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t('signup.errorPasswordMismatch'));
      return;
    }

    // Enhanced password validation (NIST guidelines)
    if (formData.password.length < 8) {
      setError(t('signup.errorPasswordLength'));
      return;
    }

    // Warn if password is weak (but don't block - let user decide)
    if (passwordStrength.score <= 2) {
      setError(t('signup.errorPasswordWeak'));
      // Don't return - allow signup but warn user
    }

    setLoading(true);

    try {
      // Map role: 'user' -> 'student', 'teacher' -> 'teacher' for backend
      const role = formData.role === 'user' ? 'student' : formData.role;

      // Use the public signup endpoint
      const API_BASE_URL = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${API_BASE_URL}/api/auth/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          email: formData.email,
          phone: toE164(formData.phoneCountry, formData.phone),
          password: formData.password,
          role: role
        })
      });

      // Check if response is ok before parsing JSON
      if (!response.ok) {
        try {
          const errorData = await response.json();
          setError(errorData.message || errorData.error || t('signup.errorCreateFailed'));
          setLoading(false);
          return;
        } catch (parseError) {
          setError(t('signup.errorConnect'));
          setLoading(false);
          return;
        }
      }

      const data = await response.json();

      if (response.ok) {
        // Automatically log in the user after successful signup
        try {
          const loginResponse = await fetch(`${API_BASE_URL}/api/auth/login`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              email: (formData.email || '').trim().toLowerCase(),
              password: formData.password
            })
          });

          const loginData = await loginResponse.json();

          if (loginResponse.ok && loginData.token) {
            // Store token and user data
            localStorage.setItem('token', loginData.token);
            localStorage.setItem('user', JSON.stringify(loginData.user));
            if (loginData.refreshToken) {
              localStorage.setItem('refreshToken', loginData.refreshToken);
            }

            // Redirect to appropriate dashboard based on role
            const role = loginData.user?.role || 'student';
            if (role === 'teacher') {
              navigate('/teacher-dashboard');
            } else if (role === 'parent') {
              navigate('/parent-dashboard');
            } else {
              navigate('/user-dashboard');
            }
          } else {
            // If auto-login fails, redirect to home with success message
            navigate('/', { state: { signupSuccess: true, message: 'Account created successfully! Please login.' } });
          }
        } catch (loginError) {
          console.error('Auto-login error:', loginError);
          // If auto-login fails, redirect to home with success message
          navigate('/', { state: { signupSuccess: true, message: 'Account created successfully! Please login.' } });
        }
      } else {
        setError(data.message || data.error || t('signup.errorCreateFailed'));
      }
    } catch (err) {
      console.error('Signup error:', err);
      // Handle network errors more gracefully
      if (err.name === 'TypeError' && err.message === 'Failed to fetch') {
        setError(t('signup.errorNetwork'));
      } else {
        setError(t('signup.errorRetry'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = useCallback(async (idToken) => {
    setError('');
    setGoogleLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id_token: idToken })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        if (data.token) localStorage.setItem('token', data.token);
        if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
        if (data.user) localStorage.setItem('user', JSON.stringify(data.user));
        const role = data.role || data.user?.role || 'student';
        if (role === 'teacher') navigate('/teacher-dashboard');
        else if (role === 'parent') navigate('/parent-dashboard');
        else navigate('/user-dashboard');
      } else {
        setError(data.message || t('signup.errorGoogle'));
      }
    } catch (err) {
      console.error('Google sign-in error:', err);
      setError(t('signup.errorTryAgain'));
    } finally {
      setGoogleLoading(false);
    }
  }, [navigate, t]);

  return (
    <div className={`signup-page${isRTL ? ' rtl' : ''}`}>
      <LandingNavbar />

      {/* Signup Section */}
      <section className="signup-section">
        <div className="signup-container">
          <div className="signup-box">
            <div className="signup-header">
              <div className="signup-icon">
                <span className="material-icons">person_add</span>
              </div>
              <h2>{t('signup.title')}</h2>
              <p className="signup-subtitle">{t('signup.subtitle')}</p>
            </div>
            
            <form onSubmit={handleSignup} className="signup-form">
              {error && (
                <div className="error-message">
                  <span className="material-icons">error</span>
                  {error}
                </div>
              )}
              
              <div className="form-group">
                <div className="input-group">
                  <span className="material-icons">person</span>
                  <input
                    type="text"
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    placeholder={t('signup.firstName')}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <div className="input-group">
                  <span className="material-icons">person</span>
                  <input
                    type="text"
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    placeholder={t('signup.lastName')}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <div className="input-group">
                  <span className="material-icons">email</span>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder={t('signup.email')}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <div className="input-group phone-group">
                  <span className="material-icons">phone</span>
                  <div className="phone-country-picker" ref={countryPickerRef}>
                    <button
                      type="button"
                      id="phoneCountry"
                      className="phone-country-select"
                      aria-label={t('signup.phoneCountry')}
                      aria-haspopup="listbox"
                      aria-expanded={countryMenuOpen}
                      onClick={() => setCountryMenuOpen((open) => !open)}
                    >
                      <span className="phone-country-iso">{formData.phoneCountry}</span>
                      <span className="phone-country-dial">+{getPhoneCountry(formData.phoneCountry).dial}</span>
                    </button>
                    {countryMenuOpen && (
                      <ul className="phone-country-list" role="listbox" aria-label={t('signup.phoneCountry')}>
                        {PHONE_COUNTRIES.map((country) => (
                          <li key={country.iso} role="presentation">
                            <button
                              type="button"
                              role="option"
                              aria-selected={formData.phoneCountry === country.iso}
                              className={`phone-country-option${formData.phoneCountry === country.iso ? ' selected' : ''}`}
                              onClick={() => handlePhoneCountryChange(country.iso)}
                            >
                              <span className="phone-country-iso">{country.iso}</span>
                              <span className="phone-country-dial">+{country.dial}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    placeholder={getPhoneCountry(formData.phoneCountry).example}
                    inputMode="numeric"
                    autoComplete="tel-national"
                    aria-required="true"
                    required
                  />
                  <span className="required-star" aria-hidden="true">*</span>
                </div>
              </div>

              <div className="form-group">
                <div className="input-group">
                  <span className="material-icons">lock</span>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder={t('signup.password')}
                    required
                  />
                </div>
                {/* Password Strength Indicator */}
                {formData.password && (
                  <div className="password-strength-meter">
                    <div className="password-strength-bar-container">
                      <div 
                        className="password-strength-bar"
                        style={{
                          width: `${(passwordStrength.score / 4) * 100}%`,
                          backgroundColor: passwordStrength.color,
                          transition: 'all 0.3s ease'
                        }}
                      />
                    </div>
                    {passwordStrength.label && (
                      <div className="password-strength-label" style={{ color: passwordStrength.color }}>
                        <span className="material-icons">
                          {passwordStrength.score <= 2 ? 'warning' : passwordStrength.score <= 3.5 ? 'info' : 'check_circle'}
                        </span>
                        {t('signup.passwordStrength')} <strong>{t(passwordStrength.label)}</strong>
                      </div>
                    )}
                    <div className="password-requirements">
                      <p className="requirements-title">{t('signup.passwordMustContain')}</p>
                      <ul>
                        <li className={passwordStrength.requirements.minLength ? 'met' : ''}>
                          <span className="material-icons">
                            {passwordStrength.requirements.minLength ? 'check_circle' : 'cancel'}
                          </span>
                          {t('signup.reqMinLength')}
                        </li>
                        <li className={passwordStrength.requirements.hasUppercase ? 'met' : ''}>
                          <span className="material-icons">
                            {passwordStrength.requirements.hasUppercase ? 'check_circle' : 'cancel'}
                          </span>
                          {t('signup.reqUppercase')}
                        </li>
                        <li className={passwordStrength.requirements.hasLowercase ? 'met' : ''}>
                          <span className="material-icons">
                            {passwordStrength.requirements.hasLowercase ? 'check_circle' : 'cancel'}
                          </span>
                          {t('signup.reqLowercase')}
                        </li>
                        <li className={passwordStrength.requirements.hasNumber ? 'met' : ''}>
                          <span className="material-icons">
                            {passwordStrength.requirements.hasNumber ? 'check_circle' : 'cancel'}
                          </span>
                          {t('signup.reqNumber')}
                        </li>
                        <li className={passwordStrength.requirements.hasSpecialChar ? 'met' : ''}>
                          <span className="material-icons">
                            {passwordStrength.requirements.hasSpecialChar ? 'check_circle' : 'cancel'}
                          </span>
                          {t('signup.reqSpecial')}
                        </li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>

              <div className="form-group">
                <div className="input-group">
                  <span className="material-icons">lock_outline</span>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    placeholder={t('signup.confirmPassword')}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <div className="input-group select-group">
                  <span className="material-icons">school</span>
                  <select
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    className="role-select"
                  >
                    <option value="user">{t('signup.student')}</option>
                    <option value="parent">{t('signup.parent')}</option>
                    <option value="teacher">{t('signup.teacher')}</option>
                  </select>
                </div>
              </div>

              <button 
                className="btn-primary full-width" 
                type="submit"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="material-icons">hourglass_empty</span>
                    {t('signup.creatingAccount')}
                  </>
                ) : (
                  <>
                    <span className="material-icons">person_add</span>
                    {t('signup.createAccount')}
                  </>
                )}
              </button>

              {process.env.REACT_APP_GOOGLE_CLIENT_ID && (
                <>
                  <div className="signup-divider">
                    <span>{t('signup.orGoogle')}</span>
                  </div>
                  <div className="signup-google-wrap">
                    <GoogleSignInButton
                      onSuccess={handleGoogleSuccess}
                      onError={() => setError(t('signup.errorGoogleStart'))}
                      disabled={loading || googleLoading}
                      text="signup_with"
                    />
                    {googleLoading && <p className="signup-google-loading">{t('signup.creatingAccount')}</p>}
                  </div>
                </>
              )}

              <div className="signup-divider">
                <span>{t('signup.or')}</span>
              </div>

              <p className="login-link">
                {t('signup.alreadyHaveAccount')}{' '}
                <Link to="/" onClick={() => {
                  setTimeout(() => {
                    const loginSection = document.getElementById('login');
                    if (loginSection) {
                      loginSection.scrollIntoView({ behavior: 'smooth' });
                    }
                  }, 100);
                }}>{t('signup.loginHere')}</Link>
              </p>

              <p className="signup-terms">
                {t('signup.termsAgree')}{' '}
                <Link to="/terms-of-service">{t('footer.termsOfService')}</Link> {t('signup.and')}{' '}
                <Link to="/privacy-policy">{t('footer.privacyPolicy')}</Link>
              </p>
            </form>
          </div>

          {/* Benefits Sidebar */}
          <div className="signup-benefits">
            <h3>{t('signup.whyJoin')}</h3>
            <div className="benefits-list">
              <div className="benefit-item">
                <span className="material-icons">verified</span>
                <div>
                  <strong>{t('signup.cefrTitle')}</strong>
                  <p>{t('signup.cefrDesc')}</p>
                </div>
              </div>
              <div className="benefit-item">
                <span className="material-icons">school</span>
                <div>
                  <strong>{t('signup.levelsTitle')}</strong>
                  <p>{t('signup.levelsDesc')}</p>
                </div>
              </div>
              <div className="benefit-item">
                <span className="material-icons">psychology</span>
                <div>
                  <strong>{t('signup.aiTitle')}</strong>
                  <p>{t('signup.aiDesc')}</p>
                </div>
              </div>
              <div className="benefit-item">
                <span className="material-icons">trending_up</span>
                <div>
                  <strong>{t('signup.progressTitle')}</strong>
                  <p>{t('signup.progressDesc')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SignupPage;

